import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import _get from 'lodash/get';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import TabBar from '@lib/components/TabBar/TabBar';
import Trans from '@lib/components/Trans/Trans';
import useSearchParam from '@lib/hooks/useSearchParam';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import getEmptyStateTextKeys from 'components/Tasks/utils/getEmptyStateTextKeys';
import getTableTabs from 'routes/Tasks/utils/getTableTabs';
import getDepartmentTasksTableColumns from '../utils/getDepartmentTasksTableColumns';
import { UseDepartmentQueriesResult } from '../utils/useDepartmentQueries';

type Props = {
  useDepartmentQueriesResult: UseDepartmentQueriesResult;
};

export default function DepartmentViewTasks({
  useDepartmentQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [searchQuery] = useSearchParam();

  const {
    useDepartmentQueryHookResult,
    useDepartmentTasksQueryHookResult,
    taskTabParam,
    setTaskTabParam,
  } = useDepartmentQueriesResult;
  const { data } = useDepartmentQueryHookResult;
  const { name } = data || {};

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DepartmentsManagement) ===
    AccessLevelEnum.Read;

  const {
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    taskCounterData,
    totalTasksCount,
  } = useDepartmentTasksQueryHookResult;

  const hasAnyLinkedTasks = [
    'active',
    'pending',
    'deadlineMissed',
    'completed',
    'archived',
  ].some((key) => (_get(taskCounterData, key, 0) as number) > 0);

  const onEditDepartment = () =>
    navigate(
      APP_URLS.dashboard.departments.edit.getDynamicPath({
        pathParts: { id },
      }),
    );

  const tableColumns = getDepartmentTasksTableColumns({ t });
  const tableTabs = getTableTabs({ t, taskCounterData });

  const editDepartmentAction = !isViewOnly ? (
    <Button
      key="edit"
      buttonText={t('edit-department')}
      buttonType={ButtonTypes.primaryFilled}
      leftIcon="edit"
      onClick={onEditDepartment}
      disabled={tasksLoading}
    />
  ) : undefined;

  const isNoLinkedTasksStateVisible =
    !hasAnyLinkedTasks && !searchQuery && !tasksError && !tasksLoading;
  const noLinkedTasksState = isNoLinkedTasksStateVisible ? (
    <ListEmptyState
      title={t('no-department-linked-tasks-yet')}
      text={t('no-department-linked-tasks-text')}
      actions={editDepartmentAction ? [editDepartmentAction] : []}
    />
  ) : null;

  const [emptyStateTitleKey, emptyStateTextKey] =
    getEmptyStateTextKeys(taskTabParam);
  const isEmptyStateVisible =
    hasAnyLinkedTasks &&
    tasks.length === 0 &&
    !searchQuery &&
    !tasksError &&
    !tasksLoading;
  const isEmptyStateByOutOfRangePage =
    tasks.length === 0 &&
    !!totalTasksCount &&
    !searchQuery &&
    !tasksError &&
    !tasksLoading;
  let emptyState: React.ReactNode = null;
  if (isEmptyStateByOutOfRangePage) {
    emptyState = <PageOutOfRangeEmptyState totalEntries={totalTasksCount} />;
  } else if (isEmptyStateVisible) {
    emptyState = (
      <ListEmptyState
        title={t(emptyStateTitleKey)}
        text={<Trans i18nKey={emptyStateTextKey} />}
      />
    );
  }

  const isErrorStateVisible = !!tasksError && !tasksLoading;
  const errorState = isErrorStateVisible ? (
    <ErrorPage
      status={getStatusFromError(tasksError)}
      description={getDescriptionFromError(tasksError)}
    />
  ) : null;

  const isContentVisible =
    !tasksError && hasAnyLinkedTasks && !tasksFirstLoading;
  const content = isContentVisible ? (
    <>
      <TabBar
        items={tableTabs}
        active={taskTabParam}
        onActiveChange={(item) => setTaskTabParam(item.value)}
      />
      {(!!tasks.length || searchQuery) && (
        <Search placeholder={t('tasks-search-placeholder')} />
      )}
      <Table
        columns={tableColumns}
        data={tasks}
        emptyState={emptyState}
        id="departmentTasks"
        loading={tasksLoading}
        totalEntries={totalTasksCount}
      />
    </>
  ) : null;

  return (
    <WhiteBox>
      <DashboardTitleHeader
        title={name}
        actions={
          isViewOnly ? (
            <ViewOnlyChip key="view" />
          ) : (
            <Button
              key="edit"
              buttonText={t('edit')}
              buttonType={ButtonTypes.primaryOutlined}
              leftIcon="edit"
              onClick={onEditDepartment}
            />
          )
        }
      />
      {tasksFirstLoading && <CircleLoader />}
      {errorState}
      {noLinkedTasksState}
      {content}
    </WhiteBox>
  );
}
